import { useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import LocationEpisode from 'models/LocationEpisode';
import {
  useDischargeCalendarSearch,
  useListViewActions,
  useListViewSort,
  useListViewState,
} from 'stores/dischargeCalendarStore';
import { BodySmall } from 'styles/typography';

import { DISCHARGE_CALENDAR_VIEWS } from '../constants';
import useDischargeCalendarData from '../hooks/useDischargeCalendarData';
import CalendarLegend from '../shared/CalendarLegend';

import listViewColumns from './listViewColumns';

export default function ListView() {
  const navigate = useNavigate();
  const pagination = usePagination({ pageSize: 10 });
  const { startDate, endDate } = useListViewState();
  const sort = useListViewSort();
  const { setSort } = useListViewActions();
  const search = useDischargeCalendarSearch();

  const { data, isFetching } = useDischargeCalendarData({
    page: pagination.paging.page,
    pageSize: pagination.paging.pageSize,
    view: DISCHARGE_CALENDAR_VIEWS.LIST,
  });

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, search]);

  const handleClickPatient = (locationEpisode: LocationEpisode) => {
    navigate(`/patients/${locationEpisode.episodeId}`);
  };

  const columns = useMemo(
    () => listViewColumns({ onClickPatient: handleClickPatient }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Container>
      <HeaderContainer>
        <Header>
          <DateRangeLabel>
            {format(startDate, 'MMMM d, yyyy')} – {format(endDate, 'MMMM d, yyyy')}
          </DateRangeLabel>
          <CalendarLegend />
        </Header>
        <BodySmall>{data?.meta?.totalRecords ?? 0} patients found</BodySmall>
      </HeaderContainer>
      <TableContainer>
        <ControlledTable<LocationEpisode>
          data={data?.data ?? []}
          columns={columns}
          loading={isFetching}
          pageCount={data?.meta?.totalPages ?? 1}
          pagination={pagination}
          sorting={{ sortBy: sort, setSortBy: setSort }}
        />
      </TableContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const DateRangeLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black100};
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;
